/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { ensureCurrentUser } from '../../../../util/data';
import { CART_UPDATED_EVENT, cartItemCount, readCartItems } from '../../../../util/cart';

import {
  AvatarLarge,
  ExternalLink,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <NamedLink name={name} params={params} to={to} className={className}>
        <span className={css.menuItemBorder} />
        {text}
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={css.navigationLink}>
      <span className={css.menuItemBorder} />
      {text}
    </ExternalLink>
  );
};

const marketplaceCurrentPageClass = (currentPage, page) => {
  const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page === 'InboxPage';
  const isSearchPage =
    page === 'SearchPage' &&
    (currentPage === 'SearchPage' || currentPage === 'SearchPageWithListingType');

  return currentPage === page || isInboxPage || isSearchPage ? css.currentPage : null;
};

const MarketplaceLinks = ({ currentPage, inboxTab, notificationCount }) => {
  const [cartCount, setCartCount] = useState(0);
  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  useEffect(() => {
    const updateCount = () => setCartCount(cartItemCount(readCartItems()));
    updateCount();
    window.addEventListener(CART_UPDATED_EVENT, updateCount);
    return () => window.removeEventListener(CART_UPDATED_EVENT, updateCount);
  }, []);

  return (
    <div className={css.marketplaceLinksWrapper}>
      <NamedLink
        className={classNames(
          css.navigationLink,
          marketplaceCurrentPageClass(currentPage, 'NewListingPage')
        )}
        name="NewListingPage"
      >
        <FormattedMessage id="TopbarMobileMenu.newListingLink" />
      </NamedLink>
      <NamedLink
        className={classNames(
          css.navigationLink,
          marketplaceCurrentPageClass(currentPage, 'SearchPage')
        )}
        name="SearchPage"
      >
        <FormattedMessage id="TopbarMobileMenu.browseListingsLink" />
      </NamedLink>
      <NamedLink
        className={classNames(
          css.navigationLink,
          marketplaceCurrentPageClass(currentPage, 'AboutUsPage')
        )}
        name="AboutUsPage"
      >
        <FormattedMessage id="TopbarMobileMenu.aboutUsLink" />
      </NamedLink>
      <NamedLink
        className={classNames(
          css.navigationLink,
          marketplaceCurrentPageClass(currentPage, 'CartPage')
        )}
        name="CartPage"
      >
        <FormattedMessage id="TopbarMobileMenu.cartLink" />
        {cartCount > 0 ? <span className={css.cartBadge}>{cartCount}</span> : null}
      </NamedLink>
      <NamedLink
        name="MessagesPage"
        className={classNames(
          css.navigationLink,
          marketplaceCurrentPageClass(currentPage, 'MessagesPage')
        )}
      >
        <FormattedMessage id="TopbarMobileMenu.messages" />
      </NamedLink>
      <NamedLink
        className={classNames(css.inbox, marketplaceCurrentPageClass(currentPage, 'InboxPage'))}
        name="InboxPage"
        params={{ tab: inboxTab }}
      >
        <FormattedMessage id="TopbarMobileMenu.inboxLink" />
        {notificationCountBadge}
      </NamedLink>
    </div>
  );
};

/**
 * Menu for mobile layout (opens through hamburger icon)
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string?} props.currentPage
 * @param {boolean} props.currentUserHasListings
 * @param {Object?} props.currentUser API entity
 * @param {number} props.notificationCount
 * @param {Array<Object>} props.customLinks Contains object like { group, text, type, href, route }
 * @param {Function} props.onLogout
 * @returns {JSX.Element} search icon
 */
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    inboxTab,
    currentUser,
    notificationCount = 0,
    customLinks,
    onLogout,
    showCreateListingsLink,
  } = props;

  const user = ensureCurrentUser(currentUser);

  const extraLinks = customLinks.map((linkConfig, index) => {
    return (
      <CustomLinkComponent
        key={`${linkConfig.text}_${index}`}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage
          id="TopbarMobileMenu.signupOrLogin"
          values={{ lineBreak: <br />, signup, login }}
        />
      </span>
    );
    return (
      <nav className={css.root}>
        <div className={css.content}>
          <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div>

          <MarketplaceLinks
            currentPage={currentPage}
            inboxTab={inboxTab}
            notificationCount={notificationCount}
          />
          <div className={css.customLinksWrapper}>{extraLinks}</div>

          <div className={css.spacer} />
        </div>
      </nav>
    );
  }

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const manageListingsLinkMaybe = showCreateListingsLink ? (
    <NamedLink
      className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
      name="ManageListingsPage"
    >
      <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
    </NamedLink>
  ) : null;

  return (
    <div className={css.root}>
      <AvatarLarge className={css.avatar} user={currentUser} />
      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>

        <div className={css.accountLinksWrapper}>
          <MarketplaceLinks
            currentPage={currentPage}
            inboxTab={inboxTab}
            notificationCount={notificationCount}
          />
          {manageListingsLinkMaybe}
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
          </NamedLink>
        </div>
        <div className={css.customLinksWrapper}>{extraLinks}</div>
        <div className={css.spacer} />
      </div>
    </div>
  );
};

export default TopbarMobileMenu;
